import { useState } from "react";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { CANCEL_EVENT_REGISTRATION_MUTATION } from "../../../queries";
import Button from "@/components/Button";
import useModal from "@/hooks/useModal";
import { ApolloError } from "@apollo/client";
import Banner from "@/components/Banner";
import { useOutletContext, useParams } from "react-router-dom";
import type { EventOutletContext } from "../../_layout";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

interface DeleteModalProps {
  eventRegistrationId: string;
  onClosed: () => void;
  refetch?: () => void;
  selectedRegistration: any;
}

export default function DeleteModal({
  eventRegistrationId,
  onClosed,
  refetch,
  selectedRegistration
}: DeleteModalProps) {
  const { Modal } = useModal();
  const outletContext = useOutletContext<EventOutletContext | undefined>();
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();

  const [deleteEventRegistration, { loading: mutationLoading }] = useMutation(CANCEL_EVENT_REGISTRATION_MUTATION);
  const [bannerError, setBannerError] = useState("");

  const handleDelete = async () => {
    try {
      const { data } = await deleteEventRegistration({
        variables: {
          id: eventRegistrationId,
          issueRefund: true,
          suspend: false
        }
      });

      const { success, message } = data.cancelEventRegistration;
      if (success) {
        refetch && refetch();
        showSnackbar("Registration deleted successfully", { close: true });
        onClosed();
        trackEvent(
          EVENT_ANALYTICS_EVENTS.registrationCanceled,
          getEventAnalyticsData({
            source: "registrations",
            eventId: outletContext?.eventData?.id,
            eventSlug,
            registrationId: eventRegistrationId,
            action: "delete",
            issueRefund: true
          })
        );
      } else {
        setBannerError(message);
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    }
  };

  const action = (
    <Button theme="error" icon="delete" disabled={mutationLoading} onClick={handleDelete}>
      Delete
    </Button>
  );

  return (
    <Modal
      action={action}
      onClosed={onClosed}
      headline="Delete Registration"
      data-testid="delete-modal"
      hasFormElements={false}
      className="w-[490px]"
    >
      {!!bannerError && (
        <div className="mb-6">
          <Banner inline variant="error" description="The registration could not be deleted." subText={bannerError} />
        </div>
      )}
      <p>Are you sure you want to delete {selectedRegistration.registrantName}’s registration from this event?</p>
    </Modal>
  );
}
